// utils/listProcessor.js
//
// Detects "common item" lists shared across survey questions (rows/columns/
// statements/values with identical content) and merges manually-saved lists
// with the same content into one entry.
//
// Design note: question -> list associations (`questionNumbers`) are always
// recomputed FRESH from the live `sdataGroup` on every call, never carried
// forward from the previous catalog. That's what makes renaming a question,
// deleting one, or editing its rows self-heal the next time this runs
// (e.g. whenever AllListsModal is opened) instead of leaving stale "used in"
// entries behind. Only metadata that ISN'T derivable from content - id,
// dependent1/dependent2 - is carried forward, keyed by content hash so it
// survives even if the list's display key changes.

// Helper function to generate list ID
const generateListId = (questionNumber, type) => {
  // Clean the question number to make it a valid ID
  const cleanQuestionNumber = String(questionNumber).replace(/[^a-zA-Z0-9_]/g, "_");
  const typeSuffix =
    type === "rows"
      ? "_rows"
      : type === "columns"
      ? "_columns"
      : type === "values"
      ? "_values"
      : "_statements";

  return `${cleanQuestionNumber}${typeSuffix}`;
};

// A question may carry its item list under the modern lowercase field
// (rows/columns/statements/values) or, for older key-based grid data, under
// the capitalized legacy field (Rows/Cols/Stats). Read whichever is present.
const getItemsForType = (questionData, type) => {
  switch (type) {
    case "rows":
      return questionData.rows || questionData.Rows || [];
    case "columns":
      return questionData.columns || questionData.Cols || [];
    case "statements":
      return questionData.statements || questionData.Statements || [];
    case "values":
      return questionData.values || questionData.Stats || [];
    default:
      return [];
  }
};

const LIST_TYPES = ["rows", "columns", "statements", "values"];

// Carry forward only what isn't derivable from live content: id + any
// per-list metadata the user set explicitly. If two previously-saved lists
// collide on the same content hash, merge their metadata instead of
// silently dropping one (the old bug this replaces).
const buildMetaByContent = (existingLists = []) => {
  const metaByContent = new Map();

  existingLists.forEach((existingList) => {
    const key = Object.keys(existingList).find((k) => Array.isArray(existingList[k]));
    if (!key) return;
    const listData = existingList[key];
    const contentHash = JSON.stringify(listData);

    const incoming = {
      id: existingList.id,
      dependent1: existingList.dependent1,
      dependent2: existingList.dependent2,
      isManual: existingList.isManual !== false,
    };

    const prior = metaByContent.get(contentHash);
    if (!prior) {
      metaByContent.set(contentHash, incoming);
    } else {
      metaByContent.set(contentHash, {
        id: prior.id || incoming.id,
        dependent1: prior.dependent1 || incoming.dependent1,
        dependent2: prior.dependent2 || incoming.dependent2,
        isManual: prior.isManual || incoming.isManual,
      });
    }
  });

  return metaByContent;
};

// Main processing function
export const processLists = (sdataGroup, existingLists = []) => {
  if (!sdataGroup || Object.keys(sdataGroup).length === 0) {
    throw new Error("No survey data found to create lists from!");
  }

  const metaByContent = buildMetaByContent(existingLists);

  // contentHash -> { listData, primaryType, questionNumbers: [{name, type}] }
  const freshByContent = new Map();

  Object.entries(sdataGroup).forEach(([questionKey, questionData]) => {
    if (!questionData || typeof questionData !== "object") return;
    const questionNumber =
      questionData.Question_number || questionData.questionNumber || questionKey;

    LIST_TYPES.forEach((type) => {
      const rawItems = getItemsForType(questionData, type);
      if (!rawItems || rawItems.length === 0) return;

      const commonItems = rawItems.filter((item) => !item.exclusive && !item.other);
      if (commonItems.length <= 1) return;

      const contentHash = JSON.stringify(commonItems);
      let entry = freshByContent.get(contentHash);
      if (!entry) {
        entry = { listData: commonItems, primaryType: type, questionNumbers: [] };
        freshByContent.set(contentHash, entry);
      }
      entry.questionNumbers.push({ name: questionNumber, type });
    });
  });

  // A list that no live question matches anymore is, by definition, dead -
  // it gets rediscovered automatically if its content ever reappears. The
  // one thing worth not losing silently is a dependent1/dependent2 the user
  // explicitly configured on it, so only THOSE orphans are kept (at
  // usageCount 0) rather than every auto-detected list living forever as
  // clutter once its last question is edited or deleted.
  existingLists.forEach((existingList) => {
    const key = Object.keys(existingList).find((k) => Array.isArray(existingList[k]));
    if (!key) return;
    if (!existingList.dependent1 && !existingList.dependent2) return;
    const listData = existingList[key];
    const contentHash = JSON.stringify(listData);
    if (!freshByContent.has(contentHash)) {
      freshByContent.set(contentHash, {
        listData,
        primaryType: existingList.type || "rows",
        questionNumbers: [],
      });
    }
  });

  let listsCreated = 0;
  let listsUpdated = 0;

  const allLists = Array.from(freshByContent.entries()).map(([contentHash, info]) => {
    const meta = metaByContent.get(contentHash) || {};
    const { questionNumbers, listData, primaryType } = info;

    const sortedQuestionNames = [...new Set(questionNumbers.map((q) => q.name))].sort();
    const uniqueTypes = [...new Set(questionNumbers.map((q) => q.type))];

    let baseKey;
    if (sortedQuestionNames.length > 1) {
      const typeIdentifier =
        uniqueTypes.length > 1 ? `[Mixed:${uniqueTypes.join("/")}]` : `[${uniqueTypes[0]}]`;
      baseKey = `${sortedQuestionNames.join("_")}${typeIdentifier}`;
    } else if (sortedQuestionNames.length === 1) {
      baseKey = `${sortedQuestionNames[0]}[${uniqueTypes[0] || primaryType}]`;
    } else {
      // Orphaned manual list - no live question matches it anymore.
      baseKey = meta.id || `orphaned[${primaryType}]_${contentHash.length}`;
    }

    if (!meta.id) {
      listsCreated += questionNumbers.length > 1 ? 1 : 0;
    } else if (questionNumbers.length > 0) {
      listsUpdated += 1;
    }

    const result = {
      [baseKey]: listData,
      type: primaryType,
      questionNumbers,
      usageCount: questionNumbers.length,
      id: meta.id || generateListId(sortedQuestionNames[0] || "list", primaryType),
    };
    if (meta.dependent1) result.dependent1 = meta.dependent1;
    if (meta.dependent2) result.dependent2 = meta.dependent2;
    if (meta.isManual) result.isManual = true;

    return result;
  });

  // A "list" only means something once 2+ questions actually share it - a
  // single question's own rows/columns/statements are just that question's
  // content, not a common-item list. The one exception is a list the user
  // explicitly configured (dependent1/dependent2 set): keep that visible
  // even if it's currently down to 0-1 live matches, same reasoning as the
  // orphan-preservation pass above - don't silently drop their config.
  const commonLists = allLists.filter(
    (entry) => entry.usageCount >= 2 || entry.dependent1 || entry.dependent2
  );

  const singleQuestionLists = commonLists.filter((list) => list.usageCount === 1);
  const multiQuestionLists = commonLists.filter((list) => list.usageCount > 1);

  return {
    allLists: commonLists,
    singleQuestionLists,
    multiQuestionLists,
    listsCreated,
    listsUpdated,
    summary: {
      total: commonLists.length,
      multiQuestion: multiQuestionLists.length,
      singleQuestion: singleQuestionLists.length,
      created: listsCreated,
      updated: listsUpdated,
    },
  };
};
